import React from 'react';
import _ from 'underscore';

import {Source} from 'constants/SourceConstants';
import {IntentLevel} from 'file-browser/FileSelectorConstants';
import FolderSelector, {UNKNOWN_FOLDER} from 'file-browser/FolderSelector';
import preferences from 'Preferences';
import {ButtonSize} from 'ui-library/buttons/Button';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

interface BaseSourceSelectorProps {
  areSourcesLoading?: boolean;
  buttonSize?: ButtonSize;
  selectedSourceId: string | null;
  showRegistries: boolean;
  sources: Array<Source>;
  readOnly?: boolean;
  level?: IntentLevel;
  enableRedesign?: boolean;
  /** Key where last selected source ID will be saved to. */
  preferenceKey?: string;
  /**
   * If the selector modal is opened from a tooltip, its z-index may need to be raised to render
   * on top of that tooltip.
   *
   * @default 1040
   */
  selectorModalZIndex?: 1040 | 1060;
}

export type NonNullableSourceSelectorProps = BaseSourceSelectorProps & {
  allowNone?: false;
  onSourceSelected: (sourceId: string) => void;
};

export type NullableSourceSelectorProps = BaseSourceSelectorProps & {
  allowNone: true;
  onSourceSelected: (sourceId: string | null) => void;
};

export type SourceSelectorProps = NonNullableSourceSelectorProps | NullableSourceSelectorProps;

export function SourceSelector({
  areSourcesLoading,
  buttonSize,
  selectedSourceId,
  showRegistries,
  sources,
  readOnly = false,
  level,
  enableRedesign = false,
  preferenceKey,
  selectorModalZIndex,
  // Keep allowNone + onSourceSelected in rest props so TypeScript preserves
  // their union relationship for narrowing in handleChangeSource.
  ...sourceSelectedProps
}: SourceSelectorProps): JSX.Element {
  if (areSourcesLoading) {
    return <CenteredHorizontalThrobber />;
  }

  let rootFolderId: string | null;
  if (selectedSourceId) {
    const selectedSource = _.findWhere(sources, {id: selectedSourceId});
    // selectedSource is null if it could not be found, e.g. if the user does not have access
    rootFolderId = selectedSource ? selectedSource.rootFolder.api_identifier : UNKNOWN_FOLDER;
  } else {
    rootFolderId = null;
  }

  function handleChangeSource(e: {target: {value: string}}): void {
    const source = sources.find((s) => s.rootFolder.api_identifier === e.target.value);
    if (sourceSelectedProps.allowNone && !source?.id && selectedSourceId != null) {
      sourceSelectedProps.onSourceSelected(null);
    } else if (source && selectedSourceId !== source.id) {
      sourceSelectedProps.onSourceSelected(source.id);
    }
    if (preferenceKey != null) {
      preferences.set(preferenceKey, source?.id ?? null);
    }
  }

  return (
    <FolderSelector
      buttonSize={buttonSize}
      noFolderText={showRegistries ? 'Choose project or registry' : 'Choose project'}
      value={rootFolderId}
      onChange={handleChangeSource}
      // The combination of showInventory=showNotebook=false is what disables sub-folder selection
      showInventory={false}
      showNotebook={false}
      showRegistries={showRegistries}
      readOnly={readOnly}
      level={level}
      enableRedesign={enableRedesign}
      allowNone={sourceSelectedProps.allowNone ?? false}
      selectorModalZIndex={selectorModalZIndex}
      unsetOnFetchFail={true}
    />
  );
}

export default SourceSelector;
